import * as Phaser from "phaser"
import { GAME_HEIGHT, GAME_WIDTH } from "../../data/constants"
import type { EnemyData, GameSprite } from "../../types"
import { type EnemyState, StatefulEnemyRuntime } from "../enemy-system"

type SnakeFsmState =
  | "entering"
  | "flashing"
  | "active"
  | "dive-track"
  | "dive-charge"
  | "dive-recover"

const HOLD_X = Math.round(GAME_WIDTH * 0.72)
/** How long the snake hunts before diving (ms) */
const DIVE_INTERVAL_BASE = 4800
/** Dive tracking phase — aligns Y with player */
const DIVE_TRACK_DURATION = 700
/** Charge phase — screams across screen */
const DIVE_CHARGE_DURATION = 900
/** Recover after exiting left edge and re-entering right */
const DIVE_RECOVER_DURATION = 1200

// Y at which the mountain ridge sits — snake depth flips when it crests this line
const HORIZON_Y = GAME_HEIGHT * 0.72
const SNAKE_SURFACE_DEPTH = 80
const SNAKE_SUBMERGED_DEPTH = 45

export class SnakeEnemy extends StatefulEnemyRuntime<SnakeFsmState> {
  private diveTimer = DIVE_INTERVAL_BASE
  private diveTargetY = GAME_HEIGHT * 0.5

  constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<SnakeFsmState>[] = [
      // ── BURROW ENTRANCE ───────────────────────────────────────────────────
      // Snake starts below the ground surface layer (depth 45 < ground 52),
      // arcs upward and right into view like the Gradius desert worm.
      // Depth flips to 80 the moment it clears the horizon.
      {
        name: "entering",
        behaviorState: "flying",
        canFire: false,
        update: (ctx) => {
          const { sprite, enemyData: ed } = ctx
          const dt = ctx.delta / 16.6
          const targetY = ed.homeY ?? GAME_HEIGHT * 0.4

          // Curve up from the ground toward the hold position
          sprite.x = Phaser.Math.Linear(sprite.x, HOLD_X, 1 - (1 - 0.055) ** dt)
          sprite.y = Phaser.Math.Linear(sprite.y, targetY, 1 - (1 - 0.04) ** dt)

          // Tilt forward as it pushes through the surface
          const riseSpeed = sprite.y - targetY
          sprite.setAngle(Phaser.Math.Clamp(riseSpeed * 0.12, -20, 20))

          // Flip depth as it clears the horizon
          if (sprite.y < HORIZON_Y) {
            sprite.setDepth(SNAKE_SURFACE_DEPTH)
          }

          if (ctx.stateTime >= 1800) return "flashing"
          return null
        },
        exit: (ctx) => {
          ctx.sprite.setDepth(SNAKE_SURFACE_DEPTH)
          ctx.sprite.setAngle(0)
        },
      },
      {
        name: "flashing",
        behaviorState: "flying",
        canFire: false,
        update: (ctx) => {
          const flashIndex = Math.floor(ctx.stateTime / 200)
          ctx.sprite.setAlpha(flashIndex % 2 === 0 ? 1 : 0.15)
          if (ctx.stateTime >= 1400) return "active"
          return null
        },
        exit: (ctx) => {
          ctx.sprite.setAlpha(1)
          const ed = ctx.enemyData
          const def = ctx.enemyDef
          if (def) ed.vx = def.vx
        },
      },
      {
        name: "active",
        behaviorState: "flying",
        canFire: true,
        update: (ctx) => {
          this.applyBaseMovement(ctx)

          // Dive more frequently when enraged (≤50% HP)
          const interval =
            ctx.hpRatio < 0.5 ? DIVE_INTERVAL_BASE * 0.65 : DIVE_INTERVAL_BASE
          this.diveTimer -= ctx.delta
          if (this.diveTimer <= 0) {
            this.diveTimer = interval + Math.random() * 1200
            this.diveTargetY = ctx.playerY + (Math.random() - 0.5) * 60
            return "dive-track"
          }

          return null
        },
      },

      // ── Dive attack ───────────────────────────────────────────────

      {
        // Phase 1: rapidly lock Y to the player before charging
        name: "dive-track",
        behaviorState: "tracking",
        canFire: false,
        enter: () => {
          this.sprite.setTint(0xff4444)
        },
        update: (ctx) => {
          const { sprite, enemyData } = ctx
          const dt = ctx.delta / 16.6
          enemyData.vx = 0

          // Aggressive Y snap
          sprite.y = Phaser.Math.Linear(
            sprite.y,
            Phaser.Math.Clamp(this.diveTargetY, 50, GAME_HEIGHT - 50),
            1 - (1 - 0.22) ** (dt * 1.8),
          )
          // Hold X position
          sprite.x = Phaser.Math.Linear(sprite.x, HOLD_X, 1 - (1 - 0.12) ** dt)

          if (ctx.stateTime >= DIVE_TRACK_DURATION) {
            sprite.clearTint()
            return "dive-charge"
          }
          return null
        },
        exit: (ctx) => {
          ctx.sprite.clearTint()
        },
      },
      {
        // Phase 2: charge across the screen left at high speed, fire ring shot midway
        name: "dive-charge",
        behaviorState: "attacking",
        canFire: true,
        enter: (ctx) => {
          ctx.enemyData.bossAttackPattern = "climax"
          ctx.enemyData.shootCooldown = Math.min(
            ctx.enemyData.shootCooldown,
            280,
          )
          // Screen reacts to the charge — tint + heat haze for full duration
          if (ctx.enemyData.bossMaxHp !== undefined) {
            ctx.vfx.sustainedScreenTint(
              0xff44aa,
              0.28,
              DIVE_CHARGE_DURATION,
              350,
            )
            ctx.vfx.heatHazePulse(0.015, DIVE_CHARGE_DURATION + 450)
          }
        },
        update: (ctx) => {
          const { sprite, enemyData } = ctx
          const dt = ctx.delta / 16.6
          const chargeSpeed = 9.5 * dt

          enemyData.vx = 0
          sprite.x -= chargeSpeed
          // Slight wave during charge
          sprite.y = Phaser.Math.Clamp(
            this.diveTargetY + Math.sin(ctx.stateTime / 80) * 12,
            50,
            GAME_HEIGHT - 50,
          )

          if (ctx.stateTime >= DIVE_CHARGE_DURATION || sprite.x < -200) {
            return "dive-recover"
          }
          return null
        },
        exit: (ctx) => {
          // Restore normal shoot cadence after dive
          const def = ctx.enemyDef
          if (def) ctx.enemyData.shootCooldown = def.shootCooldown
          ctx.enemyData.bossAttackPattern = "pressure"
        },
      },
      {
        // Phase 3: reappear on right side, fly back to hold position
        name: "dive-recover",
        behaviorState: "flying",
        canFire: false,
        enter: (ctx) => {
          ctx.sprite.x = GAME_WIDTH + 180
          ctx.sprite.y = Phaser.Math.Clamp(
            this.diveTargetY + (Math.random() - 0.5) * 120,
            60,
            GAME_HEIGHT - 60,
          )
          ctx.sprite.setAlpha(0)
          // Fade back in
          ctx.scene.tweens.add({ targets: ctx.sprite, alpha: 1, duration: 400 })
        },
        update: (ctx) => {
          const { sprite } = ctx
          const dt = ctx.delta / 16.6
          sprite.x = Phaser.Math.Linear(
            sprite.x,
            HOLD_X,
            1 - (1 - 0.1) ** (dt * 0.8),
          )

          if (
            ctx.stateTime >= DIVE_RECOVER_DURATION &&
            Math.abs(sprite.x - HOLD_X) < 40
          ) {
            return "active"
          }
          return null
        },
      },
    ]

    super(sprite, enemyData, "entering", states)
  }

  public override onSpawn(): void {
    super.onSpawn()
    // Start submerged below the ground horizon, hidden behind the foreground layer
    this.sprite.y = GAME_HEIGHT + 80
    this.sprite.setAlpha(1)
    this.sprite.setDepth(SNAKE_SUBMERGED_DEPTH)
    this.diveTimer = DIVE_INTERVAL_BASE + Math.random() * 1500
  }
}
